/**
 * Weight Input Modal
 * Used for products sold by weight (kg/litro/metro).
 * Shows weight from scale or allows manual input.
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Scale, Keyboard } from 'lucide-react';

interface WeightInputModalProps {
  isOpen: boolean;
  productName: string;
  unitPrice: number;
  unit: string;
  scaleWeight?: number;
  scaleAvailable?: boolean;
  onConfirm: (quantity: number) => void;
  onCancel: () => void;
}

export function WeightInputModal({
  isOpen,
  productName,
  unitPrice,
  unit,
  scaleWeight,
  scaleAvailable = false,
  onConfirm,
  onCancel,
}: WeightInputModalProps) {
  const [manualWeight, setManualWeight] = useState('');
  const [useManual, setUseManual] = useState(!scaleAvailable);
  const [error, setError] = useState('');

  // Update manual mode if scale becomes unavailable
  useEffect(() => {
    if (!scaleAvailable) {
      setUseManual(true);
    }
  }, [scaleAvailable]);

  const currentWeight = useManual ? parseFloat(manualWeight) || 0 : (scaleWeight || 0);
  const calculatedValue = Math.round(currentWeight * unitPrice * 100) / 100;

  const handleConfirm = () => {
    if (currentWeight <= 0) {
      setError('Informe um peso válido maior que zero');
      return;
    }
    if (currentWeight > 999.999) {
      setError('Peso máximo excedido');
      return;
    }
    // Limit to 3 decimal places
    const roundedWeight = Math.round(currentWeight * 1000) / 1000;
    onConfirm(roundedWeight);
    setManualWeight('');
    setError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const unitLabel = unit === 'kg' ? 'kg' : unit === 'litro' ? 'L' : 'm';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-sm rounded-xl border border-border/50 bg-card p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          >
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/20">
                  <Scale className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Pesagem</h2>
                  <p className="text-sm text-muted-foreground">{productName}</p>
                </div>
              </div>
              <button
                onClick={onCancel}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Price info */}
            <div className="mb-4 rounded-lg bg-muted/50 p-3">
              <p className="text-sm text-muted-foreground">
                Preço: <span className="font-medium text-foreground">R$ {unitPrice.toFixed(2)}/{unitLabel}</span>
              </p>
            </div>

            {/* Scale/Manual toggle */}
            <div className="mb-4 flex gap-2">
              {scaleAvailable && (
                <button
                  onClick={() => setUseManual(false)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    !useManual
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Scale className="h-4 w-4" />
                  Balança
                </button>
              )}
              <button
                onClick={() => setUseManual(true)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  useManual
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                <Keyboard className="h-4 w-4" />
                Manual
              </button>
            </div>

            {/* Weight display */}
            <div className="mb-4">
              {useManual ? (
                <div>
                  <label className="mb-1 block text-sm font-medium text-muted-foreground">
                    Quantidade ({unitLabel})
                  </label>
                  <input
                    type="number"
                    value={manualWeight}
                    onChange={(e) => { setManualWeight(e.target.value); setError(''); }}
                    placeholder="0.000"
                    step="0.001"
                    min="0.001"
                    max="999.999"
                    className="w-full rounded-lg border border-border bg-background px-3 py-3 text-center text-2xl font-bold text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    autoFocus
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-border bg-background p-4 text-center">
                  <p className="text-sm text-muted-foreground">Peso na balança</p>
                  <p className="text-3xl font-bold text-foreground">
                    {(scaleWeight || 0).toFixed(3)} {unitLabel}
                  </p>
                </div>
              )}
            </div>

            {/* Calculated value */}
            <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 p-4 text-center">
              <p className="text-sm text-muted-foreground">Valor calculado</p>
              <p className="text-2xl font-bold text-primary">
                R$ {calculatedValue.toFixed(2)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {currentWeight.toFixed(3)} {unitLabel} × R$ {unitPrice.toFixed(2)}
              </p>
            </div>

            {/* Error */}
            {error && (
              <p className="mb-4 text-sm text-destructive">{error}</p>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={currentWeight <= 0}
                className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Adicionar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
